import React, { useEffect, useState } from 'react';
import AnnexEditor from './AnnexEditor';
import FileDropzone from './FileDropzone';
import ImageAnnotationModal from './ImageAnnotationModal';
import apiService, { getBuildingInfo, createBuildingInfo, updateBuildingInfo, getBuildingPlans, createBuildingPlan, uploadBuildingPlan, getCloseRangeInspection, createCloseRangeInspection, updateCloseRangeInspection, getAnnexes, deleteAnnex, uploadAnnexFolder, getProjectBuildings, getBuilding, getProject, updateBuildingPlan, deleteBuildingPlan, reorderBuildingPlans, saveSitePlanAnnotations, copyBuildingInfoToProject, copyBuildingPlansToProject } from '../services/api';
import { useFacadeStore } from '../facade/store';
import './BuildingInfo.css';

// Resolve backend absolute URL for static files served from /uploads
const isDev = process.env.NODE_ENV === 'development';
const canProxy = typeof window !== 'undefined' && (window.location.port === '3000' || window.location.port === '3001');
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  ((isDev && canProxy)
    ? ''
    : (typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.hostname}:8000`
        : 'http://127.0.0.1:8000'));
const toAbsoluteUrl = (url) => {
  if (!url) return '';
  const trimmed = typeof url === 'string' ? url.trim() : url;
  return trimmed.startsWith('http') ? trimmed : `${API_BASE_URL}${trimmed}`;
};

const normalizePlanUrlKey = (url) => {
  if (!url) return '';
  let value = String(url).trim();
  if (!value) return '';
  try {
    if (/^https?:\/\//i.test(value)) {
      value = new URL(value).pathname;
    }
  } catch {
  }
  value = value.split('#')[0].split('?')[0];
  try {
    value = decodeURIComponent(value);
  } catch {
  }
  if (value.startsWith('uploads/')) return `/${value}`;
  return value;
};

const planUrlFileName = (url) => {
  const key = normalizePlanUrlKey(url) || String(url || '');
  return key ? key.split('/').pop() : '';
};

const isSamePlanUrl = (a, b) => {
  const keyA = normalizePlanUrlKey(a) || String(a || '');
  const keyB = normalizePlanUrlKey(b) || String(b || '');
  if (!keyA || !keyB) return false;
  if (keyA === keyB) return true;
  const fileA = planUrlFileName(keyA);
  const fileB = planUrlFileName(keyB);
  return Boolean(fileA && fileB && fileA === fileB);
};

const isPdfUrl = (url) => /\.pdf($|\?)/i.test(String(url || ''));

const isSupportedAnnexFile = (file) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type.startsWith('image/') || type === 'application/pdf' || name.endsWith('.pdf');
};

const annexLetterRank = (title) => {
  const match = String(title || '').match(/\bannex\s+([a-z]+)/i);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return match[1].toUpperCase().split('').reduce((total, ch) => {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) return total;
    return (total * 26) + (code - 64);
  }, 0);
};

const sortAnnexItems = (items = []) => {
  return [...items].sort((a, b) => {
    const titleA = String(a?.title || '').trim();
    const titleB = String(b?.title || '').trim();
    const rankDiff = annexLetterRank(titleA) - annexLetterRank(titleB);
    if (rankDiff !== 0) return rankDiff;
    const titleDiff = titleA.localeCompare(titleB, undefined, { sensitivity: 'base', numeric: true });
    if (titleDiff !== 0) return titleDiff;
    const createdA = Date.parse(a?.created_at || '') || 0;
    const createdB = Date.parse(b?.created_at || '') || 0;
    if (createdA !== createdB) return createdA - createdB;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

const getPlanAnnotationContext = (plan) => {
  const name = String(plan?.name || '').trim().toLowerCase();
  const description = String(plan?.description || '').trim().toLowerCase();
  if (
    name.startsWith('tapping inspection -') ||
    description.includes('tapping') ||
    description.includes('close-range')
  ) {
    return 'tapping';
  }
  return 'drone';
};

const isSection12SitePlan = (plan) => {
  const name = String(plan?.name || '').trim().toLowerCase();
  const description = String(plan?.description || '').trim().toLowerCase();
  const isElevationView = name.startsWith('elevation view -');
  const isFacade = name.startsWith('facade') || description.includes('facade');
  const isTapping = name.startsWith('tapping inspection -') || description.includes('tapping') || description.includes('close-range');
  return !isElevationView && !isFacade && !isTapping;
};

const FilePreview = ({ url, alt, width = 160, height = 100, objectFit = 'cover', onClick }) => {
  const source = toAbsoluteUrl(url);
  const commonStyle = {
    width,
    height,
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    cursor: onClick ? 'pointer' : 'default',
    background: '#f8fafc',
    overflow: 'hidden',
    display: 'block',
  };

  return (
    <div style={commonStyle} onClick={onClick}>
      {isPdfUrl(url) ? (
        <iframe
          src={source}
          title={alt || 'PDF preview'}
          style={{ width: '100%', height: '100%', border: 0, background: '#fff', pointerEvents: 'none' }}
        />
      ) : (
        <img
          src={source}
          alt={alt}
          style={{ width: '100%', height: '100%', objectFit, display: 'block' }}
        />
      )}
    </div>
  );
};

// Lightweight drag-and-drop image dropzone with previews for 1.3 table
const FacadeImageDropzone = ({ images = [], onChange }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [previewUrls, setPreviewUrls] = React.useState([]);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    // Clean up old previews
    previewUrls.forEach((u) => URL.revokeObjectURL(u));
    if (images && images.length > 0) {
      const urls = images.map((f) => URL.createObjectURL(f));
      setPreviewUrls(urls);
      return () => urls.forEach((u) => URL.revokeObjectURL(u));
    } else {
      setPreviewUrls([]);
    }
  }, [images]);

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const selected = Array.from(dt.files || []).filter((f) => f.type.startsWith('image/'));
      if (selected.length > 0) { onChange([...(images || []), ...selected]); }
    }
  };
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    if (selected.length > 0) { onChange([...(images || []), ...selected]); }
  };
  const removeAt = (idx) => {
    const next = [...(images || [])];
    next.splice(idx, 1);
    onChange(next);
  };
  const clearAll = () => { onChange([]); };

  return (
    <div>
      <div
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: '1px dashed #d1d5db',
          borderRadius: 8,
          padding: 12,
          textAlign: 'center',
          background: isDragging ? 'rgba(37,99,235,0.06)' : 'rgba(0,0,0,0.02)',
          cursor: 'pointer',
        }}
      >
        {previewUrls.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            Drag & drop images here, or click to select
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
              {previewUrls.map((url, idx) => (
                <div key={idx} style={{ position: 'relative', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                  <img src={url} alt={`Preview ${idx + 1}`} style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                  <button className="btn" style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); removeAt(idx); }}>Remove</button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button className="btn" onClick={(e) => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); }}>Add more</button>
              <button className="btn" onClick={(e) => { e.stopPropagation(); clearAll(); }}>Clear all</button>
            </div>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>
    </div>
  );
};

const fields = [
  { key: 'dev_name_address', label: 'Development/building address and name' },
  { key: 'blocks_count', label: 'Number of building blocks in the development' },
  { key: 'block_age_years', label: 'Age of each building block within the development' },
  { key: 'block_height_m', label: 'Height of each building block within the development' },
  { key: 'storeys_count', label: 'Number of storey in each building block within the development' },
  { key: 'main_usage', label: 'Description of main usage of the buildings' },
  { key: 'as_built_records', label: 'Details of as-built records/drawings of facade systems' },
  { key: 'maintenance_history', label: 'Maintenance history of the facade' },
  { key: 'known_past_defects', label: 'Known defects from past PFI report' },
  { key: 'resurfacing_past_defects', label: 'Resurfacing of past defects, if any' },
];

const DEFAULT_FACADE_AREA_ROWS = [
  { elevation: 'Front', total_length_m: 29, height_m: 35, total_area_m2: 1015, tactile_point: 1, tactile_length_m: 3, tactile_height_m: 35, min_10_area_m2: 105 },
  { elevation: 'Rear', total_length_m: 30, height_m: 35, total_area_m2: 1050, tactile_point: 2, tactile_length_m: 6, tactile_height_m: 35, min_10_area_m2: 210 },
  { elevation: 'Left', total_length_m: 25, height_m: 35, total_area_m2: 875, tactile_point: 1, tactile_length_m: 3, tactile_height_m: 35, min_10_area_m2: 105 },
  { elevation: 'Right', total_length_m: 25, height_m: 35, total_area_m2: 875, tactile_point: 1, tactile_length_m: 3, tactile_height_m: 35, min_10_area_m2: 105 },
];

const DEFAULT_FACADE_AREA_NOTES = '<p><u>Notes:</u> Dimensions per tactile is 3 meter</p><ol><li><p>Front Elevation</p><p>= 105/1015</p><p>= 11% &gt; Minimum 10% closed-range inspection</p></li><li><p>Rear Elevation</p><p>= 210/1050</p><p>= 20% &gt; Minimum 10% closed-ranged inspection</p></li><li><p>Left Elevation</p><p>= 105/875</p><p>= 12% &gt; Minimum 10% closed-range inspection</p></li><li><p>Right Elevation</p><p>= 105/875</p><p>= 12% &gt; Minimum 10% closed-range inspection</p></li></ol>';

const parseFacadeAreaRows = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const serializeFacadeAreaRows = (rows) => JSON.stringify(rows || []);

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCalcNumber = (value) => {
  const parsed = toNumber(value);
  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(2);
};

const normalizeFacadeAreaRow = (row = {}) => {
  const totalLength = toNumber(row.total_length_m);
  const height = toNumber(row.height_m);
  const tactilePoint = toNumber(row.tactile_point);
  const tactileLength = toNumber(row.tactile_length_m || (tactilePoint * 3));
  const tactileHeight = toNumber(row.tactile_height_m || height);
  const totalArea = toNumber(row.total_area_m2 || (totalLength * height));
  const minArea = toNumber(row.min_10_area_m2 || (tactileLength * tactileHeight));
  return {
    elevation: row.elevation || '',
    total_length_m: totalLength,
    height_m: height,
    total_area_m2: totalArea,
    tactile_point: tactilePoint,
    tactile_length_m: tactileLength,
    tactile_height_m: tactileHeight,
    min_10_area_m2: minArea,
  };
};

const RichTextEditor = ({ value, onChange }) => {
  const editorRef = React.useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const applyFormat = (command, commandValue = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current.innerHTML);
  };

  return (
    <div className="rich-editor">
      <div className="rich-toolbar">
        <button type="button" className="btn" onClick={() => applyFormat('bold')}>B</button>
        <button type="button" className="btn" onClick={() => applyFormat('italic')}>I</button>
        <button type="button" className="btn" onClick={() => applyFormat('underline')}>U</button>
        <button type="button" className="btn" onClick={() => applyFormat('insertOrderedList')}>1.</button>
        <button type="button" className="btn" onClick={() => applyFormat('insertUnorderedList')}>-</button>
      </div>
      <div
        ref={editorRef}
        className="rich-editor-surface"
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
      />
    </div>
  );
};

const BuildingInfo = ({ buildingId, projectId, projectName, onBack }) => {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [plans, setPlans] = useState([]);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanDesc, setNewPlanDesc] = useState('');
  const [newPlanFiles, setNewPlanFiles] = useState([]);
  const [planFilter, setPlanFilter] = useState('');
  const [editingGroupKey, setEditingGroupKey] = useState(null);
  const [editGroupTitle, setEditGroupTitle] = useState('');
  const [editGroupDesc, setEditGroupDesc] = useState('');
  const [activeTab, setActiveTab] = useState('1.1');
  const [scope, setScope] = useState('building'); // 'building' | 'project'
  const [projectBuildings, setProjectBuildings] = useState([]);
  const [buildingNameById, setBuildingNameById] = useState({});
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [projectAggLoading, setProjectAggLoading] = useState(false);
  const [inspection, setInspection] = useState({
    time_dates: '',
    duration: '',
    location: '',
    methodology: '',
    participants: '',
    inspector_particulars: '',
    non_coverage_description: '',
    discrepancies: '',
    covered_all: true,
  });

  // Date/time pickers (local UI state) for 2.1 and 3.1
  const [inspectionStart, setInspectionStart] = useState('');
  const [closeRangeStart, setCloseRangeStart] = useState('');

  // Helper to format datetime-local value to a readable string, e.g., "5 Apr 2025, 09:00"
  const formatDateTimeLocal = (value) => {
    if (!value) return '';
    // value is expected as 'YYYY-MM-DDTHH:mm'
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const updateInspectionTimeDatesFromPickers = (start) => {
    const s = formatDateTimeLocal(start);
    setInspection((prev) => ({ ...prev, time_dates: s }));
  };

  const updateCloseRangeTimeDatesFromPickers = (start) => {
    const s = formatDateTimeLocal(start);
    setCloseRange((prev) => ({ ...prev, time_dates: s }));
  };
  
  const [annexList, setAnnexList] = useState([]);
  const [annexMaster, setAnnexMaster] = useState([]);
  const [uas, setUas] = useState({
    flight_location: '',
    flight_datetime: '',
    purpose: 'Building Facade Inspection',
    caas_activity_permit: '',
    areas_of_operation: '',
    media_file_format: 'Still image',
    uas_model: '',
    tethered_ua: 'No',
    client_information: '',
    client_address: '',
  });
  const [annexTitle, setAnnexTitle] = useState('');
  const [annexContent, setAnnexContent] = useState('');
  const [annexFiles, setAnnexFiles] = useState([]);
  const [annexFilter, setAnnexFilter] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxGroup, setLightboxGroup] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [projectPlans, setProjectPlans] = useState([]);
  const [projectAnnexList, setProjectAnnexList] = useState([]);
  const [annotateOpen, setAnnotateOpen] = useState(false);
  const [annotateImages, setAnnotateImages] = useState([]);
  const [annotateIndex, setAnnotateIndex] = useState(0);
  const [sitePlanImageUrl, setSitePlanImageUrl] = useState('');

  const dragIndexRef = React.useRef(null);
  const overIndexRef = React.useRef(null);
  const dragGroupRef = React.useRef(null);

  const reorderGroup = async (source, setSource, groupTitle, from, to) => {
    if (from == null || to == null || from === to) return;
    const items = source.filter(it => ((it.name || 'Untitled').trim()) === groupTitle);
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    const byId = arr.reduce((acc, it) => { acc[it.id] = it; return acc; }, {});
    const next = source.map(it => {
      const key = (it.name || 'Untitled').trim();
      if (key !== groupTitle) return it;
      const repl = byId[it.id];
      return repl || it;
    });
    setSource(next);

    try {
      const orders = arr.map((it, idx) => ({ id: it.id, order_position: idx + 1 }));
      if (orders.length > 0) {
        await reorderBuildingPlans(effectiveBuildingId, orders);
      }
    } catch (e) {
      console.error('Failed to persist plan reorder', e);
    }
  };

  const openAnnotateForGroup = (groupItems, startIdx = 0) => {
    const imgs = (groupItems || [])
      .filter((p) => !!p.file_url && !String(p.file_url).match(/\.pdf$/i))
      .map((p) => ({ 
        id: `building_plan_${p.id}`,
        plan_id: p.id,
        source_type: 'building_plan',
        src: toAbsoluteUrl(p.file_url), 
        url: toAbsoluteUrl(p.file_url),
        file_url: p.file_url, // Keep relative URL for storage key
        name: p.name,
        building_id: p.building_id || p._building_id, // Capture building ID
        annotation_context: getPlanAnnotationContext(p),
      }));
    if (imgs.length === 0) return;
    setAnnotateImages(imgs);
    setAnnotateIndex(Math.min(Math.max(0, startIdx), imgs.length - 1));
    setAnnotateOpen(true);
  };

  const effectiveBuildingId = scope === 'project' ? (selectedBuildingId || buildingId) : buildingId;

  const sitePlanCandidates = React.useMemo(() => {
    if (!plans || plans.length === 0) return [];
    const relevantPlans = plans.filter(isSection12SitePlan);
    const urls = [];
    relevantPlans.forEach((plan) => {
      if (plan.file_url && !String(plan.file_url).match(/\.pdf$/i)) {
        urls.push(toAbsoluteUrl(plan.file_url));
      }
    });
    return urls;
  }, [plans]);

  useEffect(() => {
    if (!effectiveBuildingId) {
      setSitePlanImageUrl('');
      return;
    }
    try {
      const stored = localStorage.getItem(`site_plan_${effectiveBuildingId}`);
      if (stored) {
        setSitePlanImageUrl(stored);
      } else {
        setSitePlanImageUrl('');
      }
    } catch (e) {
      console.warn('Failed to load site plan selection', e);
      setSitePlanImageUrl('');
    }
  }, [effectiveBuildingId]);

  const handleChangeSitePlan = (url) => {
    const next = url || '';
    setSitePlanImageUrl(next);
    try {
      if (effectiveBuildingId) {
        if (next) {
          localStorage.setItem(`site_plan_${effectiveBuildingId}`, next);
        } else {
          localStorage.removeItem(`site_plan_${effectiveBuildingId}`);
        }
      }
    } catch (e) {
      console.warn('Failed to persist site plan selection', e);
    }
  };

  const tabs = [
    { key: '1.1', label: '1.1 General Information' },
    { key: '1.2', label: '1.2 Overview of building elevations' },
    { key: '1.3', label: '1.3 Description of facade system' },
    { key: '1.5', label: '1.5 Tapping Inspection' },
    { key: 'FacadeArea', label: 'Facade Area & 10%' },
    { key: '2.1', label: '2.1 Diary and Scope of Visual Inspection' },
    { key: '3.1', label: '3.1 Diary and Scope of Close-range Inspection' },
    { key: 'Annex', label: 'Annex' },
    { key: 'UAS', label: 'UAS Inspection Report' },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let buildingAddress = '';
        let data = null;
        try {
          data = await getBuildingInfo(effectiveBuildingId);
        } catch (e) {
          if (e && e.status === 404) {
            const defaults = {
              dev_name_address: '',
              blocks_count: 1,
              block_age_years: 0,
              block_height_m: 0.0,
              storeys_count: 0,
              main_usage: '',
              as_built_records: '',
              maintenance_history: '',
              known_past_defects: '',
              resurfacing_past_defects: '',
              facade_area_rows: serializeFacadeAreaRows(DEFAULT_FACADE_AREA_ROWS),
              facade_area_notes: DEFAULT_FACADE_AREA_NOTES,
            };
            try {
              data = await createBuildingInfo(effectiveBuildingId, defaults);
            } catch (ce) {
              throw ce;
            }
          } else {
            throw e;
          }
        }
        setInfo(data);
        setForm(data || {});

        // Prefill address/name with building name if not set yet
        try {
          const building = await getBuilding(effectiveBuildingId);
          const buildingName = building?.name || '';
          buildingAddress = building?.address || '';
          setForm(prev => {
            const current = prev || {};
            // Only set if empty/undefined to avoid overwriting user-entered value
            const shouldPrefill = !current.dev_name_address || String(current.dev_name_address).trim().length === 0;
            return shouldPrefill ? { ...current, dev_name_address: buildingName } : current;
          });
        } catch (e) {
          // If building fetch fails, skip prefill silently
          console.warn('Could not fetch building details for prefill:', e);
        }
        // Prefill 2.1 Location of inspection from project address when empty
        try {
          if (projectId) {
            const project = await getProject(projectId);
            const projectAddress = (project?.address_detail || '').trim();
            if (projectAddress) {
              setInspection(prev => {
                const currentLocation = (prev?.location || '').trim();
                if (!currentLocation) {
                  return { ...prev, location: projectAddress };
                }
                return prev;
              });
            }
          }
        } catch (e) {
          console.warn('Could not fetch project for location prefill:', e);
        }
        // Prefill 1.1 textareas with a default "N/A" when empty
        setForm(prev => {
          const current = prev || {};
          const ensureNA = (val) => (val && String(val).trim().length > 0) ? val : 'N/A';
          return {
            ...current,
            as_built_records: ensureNA(current.as_built_records),
            maintenance_history: ensureNA(current.maintenance_history),
            known_past_defects: ensureNA(current.known_past_defects),
            resurfacing_past_defects: ensureNA(current.resurfacing_past_defects),
            main_usage: ensureNA(current.main_usage),
            facade_area_rows: current.facade_area_rows || serializeFacadeAreaRows(DEFAULT_FACADE_AREA_ROWS),
            facade_area_notes: current.facade_area_notes || DEFAULT_FACADE_AREA_NOTES,
          };
        });
        const planList = await getBuildingPlans(effectiveBuildingId);
        setPlans(planList || []);
        // Load 3.1 inspection from backend if exists
        const cr = await getCloseRangeInspection(effectiveBuildingId).catch(() => null);
        if (cr) {
          setCloseRange(cr);
        }
        // Load annex list
        const annexes = await getAnnexes(effectiveBuildingId).catch(() => []);
        setAnnexList(annexes || []);
        const uasResp = await apiService.getUASReport(effectiveBuildingId).catch(() => ({}));
        setUas({
          flight_location: uasResp.flight_location || buildingAddress || '',
          flight_datetime: uasResp.flight_datetime || '',
          purpose: uasResp.purpose || 'Building Facade Inspection',
          caas_activity_permit: uasResp.caas_activity_permit || '',
          areas_of_operation: uasResp.areas_of_operation || '',
          media_file_format: uasResp.media_file_format || 'Still image',
          uas_model: uasResp.uas_model || '',
          tethered_ua: uasResp.tethered_ua || 'No',
          client_information: uasResp.client_information || '',
          client_address: uasResp.client_address || buildingAddress || '',
        });
        setError('');
      } catch (e) {
        console.error('Error loading building info:', e);
        setError('Failed to load building info');
      } finally {
        setLoading(false);
      }
    };
    if (effectiveBuildingId) {
      load();
    }
  }, [effectiveBuildingId]);
  
  useEffect(() => {
    const loadMaster = async () => {
      try {
        const resp = await apiService.listAnnexMaster();
        setAnnexMaster(resp.items || []);
      } catch (e) {
        setAnnexMaster([]);
      }
    };
    loadMaster();
  }, []);

  // Load project buildings and aggregate plans/annexes when viewing by project
  useEffect(() => {
    const loadProjectScope = async () => {
      if (!projectId) return;
      try {
        setProjectAggLoading(true);
        const buildings = await getProjectBuildings(projectId).catch(() => []);
        setProjectBuildings(buildings || []);
        const nameMap = {};
        (buildings || []).forEach(b => { nameMap[b.id] = b.name; });
        setBuildingNameById(nameMap);
        // Aggregate plans and annexes
        const planPromises = (buildings || []).map(b => getBuildingPlans(b.id).catch(() => []));
        const annexPromises = (buildings || []).map(b => getAnnexes(b.id).catch(() => []));
        const planResults = await Promise.all(planPromises);
        const annexResults = await Promise.all(annexPromises);
        const aggregatedPlans = planResults.flat().map(p => ({ ...p, _building_id: p.building_id, _building_name: nameMap[p.building_id] }));
        const aggregatedAnnexes = annexResults.flat().map(a => ({ ...a, _building_id: a.building_id, _building_name: nameMap[a.building_id] }));
        setProjectPlans(aggregatedPlans);
        setProjectAnnexList(aggregatedAnnexes);
        // Default selected building for 1.1 info
        if (!selectedBuildingId && buildings && buildings.length > 0) {
          setSelectedBuildingId(buildings[0].id);
        }
      } catch (e) {
        console.error('Error loading project scope:', e);
        setError('Failed to load project data');
      } finally {
        setProjectAggLoading(false);
      }
    };
    if (scope === 'project') {
      loadProjectScope();
    }
  }, [scope, projectId]);

  // Load 2.1 visual inspection diary from database
  useEffect(() => {
    if (!effectiveBuildingId) return;
    const load = async () => {
      try {
        const saved = await apiService.getVisualInspectionDiary(effectiveBuildingId).catch(() => null);
        if (saved) {
          setInspection(prev => ({ ...prev, ...saved }));
        }
      } catch (e) {
        console.warn('Failed to load visual inspection diary', e);
      }
    };
    load();
  }, [effectiveBuildingId]);

  // Prefill 2.1 inspection textareas with defaults when empty
  useEffect(() => {
    setInspection(prev => {
      const ensure = (v, def) => (v && String(v).trim().length > 0) ? v : def;
      const next = {
        ...prev,
        methodology: ensure(prev?.methodology, 'Drone'),
        participants: ensure(prev?.participants, 'MaxzRange Pte Ltd\nUnmanned Aircraft Operator Permit No.: UOP/0644\n6, Tagore Drive, #02-14, Tagore Building,\nSingapore 787623'),
        non_coverage_description: ensure(prev?.non_coverage_description, 'N/A'),
        discrepancies: ensure(prev?.discrepancies, 'N/A'),
      };
      if (
        next.methodology === prev?.methodology &&
        next.participants === prev?.participants &&
        next.non_coverage_description === prev?.non_coverage_description &&
        next.discrepancies === prev?.discrepancies
      ) {
        return prev;
      }
      return next;
    });
  }, [effectiveBuildingId, inspection.methodology, inspection.participants, inspection.non_coverage_description, inspection.discrepancies]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const getFacadeAreaRows = () => {
    const rows = parseFacadeAreaRows(form?.facade_area_rows).map(normalizeFacadeAreaRow);
    return rows.length > 0 ? rows : DEFAULT_FACADE_AREA_ROWS.map(normalizeFacadeAreaRow);
  };

  const setFacadeAreaRows = (rows) => {
    handleChange('facade_area_rows', serializeFacadeAreaRows(rows));
  };

  const updateFacadeAreaRow = (index, key, value) => {
    const rows = getFacadeAreaRows();
    const next = rows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    ));
    setFacadeAreaRows(next);
  };

  const addFacadeAreaRow = () => {
    setFacadeAreaRows([
      ...getFacadeAreaRows(),
      { elevation: '', total_length_m: 0, height_m: 0, total_area_m2: 0, tactile_point: 1, tactile_length_m: 3, tactile_height_m: 0, min_10_area_m2: 0 },
    ]);
  };

  const removeFacadeAreaRow = (index) => {
    const next = getFacadeAreaRows().filter((_, rowIndex) => rowIndex !== index);
    setFacadeAreaRows(next.length > 0 ? next : [{ elevation: '', total_length_m: 0, height_m: 0, total_area_m2: 0, tactile_point: 1, tactile_length_m: 3, tactile_height_m: 0, min_10_area_m2: 0 }]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess('');
      setError('');
      let result;
      // Only include allowed fields to match backend schema
      const allowedKeys = [
        ...fields.map(f => f.key),
        'facade_area_rows',
        'facade_area_notes',
      ];
      const payload = allowedKeys.reduce((acc, key) => {
        acc[key] = form?.[key];
        return acc;
      }, {});
      if (info && info.id) {
        result = await updateBuildingInfo(effectiveBuildingId, payload);
      } else {
        result = await createBuildingInfo(effectiveBuildingId, payload);
      }
      setInfo(result);
      setForm(result);
      setSuccess('Saved successfully');
    } catch (e) {
      console.error('Error saving building info:', e);
      setError('Failed to save building info');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyInfoToProject = async () => {
    if (!effectiveBuildingId) return;
    if (!window.confirm('Are you sure you want to copy this building info (1.1) to ALL other buildings in this project? This will overwrite their existing info.')) return;
    
    try {
      setSaving(true);
      await copyBuildingInfoToProject(effectiveBuildingId);
      setSuccess('Building info copied to all project buildings successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to copy info to project.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPlansToProject = async () => {
    if (!effectiveBuildingId) return;
    if (!window.confirm('Are you sure you want to copy these building plans (1.2) to ALL other buildings in this project? This will create copies or update existing plans.')) return;

    try {
      setSaving(true);
      await copyBuildingPlansToProject(effectiveBuildingId);
      setSuccess('Building plans copied to all project buildings successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to copy plans to project.');
    } finally {
      setSaving(false);
    }
  };

  const handleInspectionChange = (key, value) => {
    setInspection(prev => ({ ...prev, [key]: value }));
  };

  // 1.3 Facade system – use shared store
  const facade = useFacadeStore(effectiveBuildingId);

  const handleSaveInspectionDraft = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await apiService.saveVisualInspectionDiary(effectiveBuildingId, {
        time_dates: inspection.time_dates,
        duration: inspection.duration,
        location: inspection.location,
        methodology: inspection.methodology,
        participants: inspection.participants,
        inspector_particulars: inspection.inspector_particulars,
        non_coverage_description: inspection.non_coverage_description,
        discrepancies: inspection.discrepancies,
        covered_all: inspection.covered_all,
      });
      setSuccess('Saved to database');
    } catch (e) {
      console.error('Error saving visual inspection diary:', e);
      setError('Failed to save to database');
    } finally {
      setSaving(false);
    }
  };

  // 3.1 Close-range inspection state
  const [closeRange, setCloseRange] = useState({
    basis_selection: '',
    drawings_plan: '',
    time_dates: '',
    duration: '',
    methodology: '',
    inspector_particulars: '',
  });

  const handleCloseRangeChange = (key, value) => {
    setCloseRange(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveCloseRange = async () => {
    try {
      setSaving(true);
      setSuccess('');
      setError('');
      const payload = {
        basis_selection: closeRange.basis_selection,
        drawings_plan: closeRange.drawings_plan,
        time_dates: closeRange.time_dates,
        duration: closeRange.duration,
        methodology: closeRange.methodology,
        inspector_particulars: closeRange.inspector_particulars,
      };
      // If it has id then update, else create
      let result;
      if (closeRange && closeRange.id) {
        result = await updateCloseRangeInspection(effectiveBuildingId, payload);
      } else {
        result = await createCloseRangeInspection(effectiveBuildingId, payload);
      }
      setCloseRange(result);
      setSuccess('Close-range inspection saved');
    } catch (e) {
      console.error('Error saving close-range inspection:', e);
      setError('Failed to save close-range inspection');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePlan = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      // Create plan metadata first
      const created = await createBuildingPlan(effectiveBuildingId, { name: newPlanName, description: newPlanDesc });
      // Upload files if provided (one record per file with same title)
      if (newPlanFiles && newPlanFiles.length > 0) {
        for (const f of newPlanFiles) {
          await uploadBuildingPlan(effectiveBuildingId, f, newPlanName, newPlanDesc);
        }
      }
      const planList = await getBuildingPlans(effectiveBuildingId);
      setPlans(planList || []);
      setNewPlanName('');
      setNewPlanDesc('');
      setNewPlanFiles([]);
      setSuccess('Plan added successfully');
    } catch (e) {
      console.error('Error creating plan:', e);
      setError('Failed to add plan');
    } finally {
      setSaving(false);
    }
  };

  // 1.3 Facade – 1.2-like flow (add facade and list)
  const [newFacadeFiles, setNewFacadeFiles] = useState([]);
  const [newFacadeElevation, setNewFacadeElevation] = useState('');
  const [newFacadeType, setNewFacadeType] = useState('');
  const [newFacadeConnection, setNewFacadeConnection] = useState('');
  const [facadeFilter, setFacadeFilter] = useState('');
  const handleAddFacade = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      // Compute title based on elevation/type/connection, fallback to user title
      const parts = [newFacadeElevation, newFacadeType, newFacadeConnection]
        .map((v) => (v || '').trim())
        .filter(Boolean);
      const computedTitle = parts.length
        ? `Facade ${parts.join(' - ')}`
        : 'Facade';
      const descLines = [];
      if ((newFacadeElevation || '').trim()) descLines.push(`Elevation: ${newFacadeElevation.trim()}`);
      if ((newFacadeType || '').trim()) descLines.push(`Type: ${newFacadeType.trim()}`);
      if ((newFacadeConnection || '').trim()) descLines.push(`Connection: ${newFacadeConnection.trim()}`);
      const combinedDesc = descLines.join('\n');

      // Create facade metadata first (reusing building plan model)
      const created = await createBuildingPlan(effectiveBuildingId, { name: computedTitle, description: combinedDesc });
      // Upload files if provided
      if (newFacadeFiles && newFacadeFiles.length > 0) {
        for (const f of newFacadeFiles) {
          await uploadBuildingPlan(effectiveBuildingId, f, computedTitle, combinedDesc);
        }
      }
      const planList = await getBuildingPlans(effectiveBuildingId);
      setPlans(planList || []);
      setNewFacadeFiles([]);
      setNewFacadeElevation('');
      setNewFacadeType('');
      setNewFacadeConnection('');
      setSuccess('Facade added successfully');
    } catch (e) {
      console.error('Error adding facade:', e);
      setError('Failed to add facade');
    } finally {
      setSaving(false);
    }
  };

  // 1.2b Elevation Views
  const [newElevationViewName, setNewElevationViewName] = useState('');
  const [newElevationViewFiles, setNewElevationViewFiles] = useState([]);

  // 1.5 Tapping Inspection
  const [newTappingName, setNewTappingName] = useState('');
  const [newTappingFiles, setNewTappingFiles] = useState([]);

  const handleAddElevationView = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const title = `Elevation View - ${newElevationViewName}`;
      // Create plan metadata
      await createBuildingPlan(effectiveBuildingId, { name: title, description: '' });
      // Upload files
      if (newElevationViewFiles.length > 0) {
        for (const f of newElevationViewFiles) {
          await uploadBuildingPlan(effectiveBuildingId, f, title, '');
        }
      }
      const planList = await getBuildingPlans(effectiveBuildingId);
      setPlans(planList || []);
      setNewElevationViewName('');
      setNewElevationViewFiles([]);
      setSuccess('Elevation view added');
    } catch (e) {
      console.error('Error adding elevation view:', e);
      setError('Failed to add elevation view');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTappingView = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const title = `Tapping Inspection - ${newTappingName}`;
      // Create plan metadata
      await createBuildingPlan(effectiveBuildingId, { name: title, description: '' });
      // Upload files
      if (newTappingFiles.length > 0) {
        for (const f of newTappingFiles) {
          await uploadBuildingPlan(effectiveBuildingId, f, title, '');
        }
      }
      const planList = await getBuildingPlans(effectiveBuildingId);
      setPlans(planList || []);
      setNewTappingName('');
      setNewTappingFiles([]);
      setSuccess('Tapping inspection added');
    } catch (e) {
      console.error('Error adding tapping inspection:', e);
      setError('Failed to add tapping inspection');
    } finally {
      setSaving(false);
    }
  };

  const plansToShow = scope === 'project' ? projectPlans : plans;
  const annexesToShow = scope === 'project' ? projectAnnexList : annexList;
  const sortedAnnexesToShow = React.useMemo(() => sortAnnexItems(annexesToShow), [annexesToShow]);
  const hasValidAnnexUpload = React.useMemo(
    () => (annexFiles || []).some(isSupportedAnnexFile),
    [annexFiles]
  );

  const refreshAnnexList = async () => {
    if (scope === 'project') {
      const buildings = projectBuildings || [];
      const nameMap = buildingNameById || {};
      const annexPromises = buildings.map(b => getAnnexes(b.id).catch(() => []));
      const annexResults = await Promise.all(annexPromises);
      const aggregatedAnnexes = annexResults.flat().map(a => ({ ...a, _building_id: a.building_id, _building_name: nameMap[a.building_id] }));
      setProjectAnnexList(aggregatedAnnexes);
      return;
    }
    const annexes = await getAnnexes(effectiveBuildingId);
    setAnnexList(annexes || []);
  };

  const handleDeleteAnnexItems = async (items) => {
    const deletableItems = (items || []).filter((item) => item?.id);
    if (deletableItems.length === 0) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      for (const item of deletableItems) {
        await deleteAnnex(item.building_id || item._building_id || effectiveBuildingId, item.id);
      }
      await refreshAnnexList();
      setSuccess(deletableItems.length === 1 ? 'Annex deleted successfully' : 'Annex group deleted successfully');
    } catch (err) {
      console.error('Delete annex failed:', err);
      setError(err?.message || 'Failed to delete annex');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="building-info"><div className="loading">Loading...</div></div>;
  }

  return (
    <>
      <div className="tabs">
        {tabs.map((t) => (
          <button
            type="button"
            key={t.key}
            className={`tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="banner error">{error}</div>}
      {success && <div className="banner success">{success}</div>}

      {activeTab === '1.1' && (
        <>
          <div className="page-header">
            <h3>1.1 General Information of the Building{projectName ? ` — ${projectName}` : ''}</h3>
            <div className="actions">
              {onBack && (
                <button onClick={onBack} className="btn secondary">Back</button>
              )}
              <button onClick={handleSave} disabled={saving} className="btn primary">{saving ? 'Saving...' : 'Save'}</button>
              {projectId && (
                <button onClick={handleCopyInfoToProject} disabled={saving} className="btn secondary" style={{ marginLeft: 8 }} title="Copy this info to all other buildings in the project">
                  Copy to All Buildings
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px 12px' }}>
            <span style={{ fontSize: 13, color: '#555' }}>Viewing by:</span>
            <button className={`btn ${scope === 'building' ? 'primary' : ''}`} onClick={() => setScope('building')}>Building</button>
            <button className={`btn ${scope === 'project' ? 'primary' : ''}`} onClick={() => setScope('project')} disabled={!projectId}>
              Project{projectId ? ` #${projectId}` : ''}
            </button>
          </div>

          {scope === 'project' && projectId && (
            <div style={{ padding: '0 16px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#555' }}>Select building to view 1.1 info:</span>
                <select
                  value={selectedBuildingId || ''}
                  onChange={(e) => setSelectedBuildingId(parseInt(e.target.value, 10))}
                  className="cell-input"
                  style={{ maxWidth: 260 }}
                >
                  {(projectBuildings || []).map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {projectAggLoading && <span style={{ fontSize: 12, color: '#888' }}>Loading project data…</span>}
              </div>
            </div>
          )}

          <div className="info-card">
            <div className="info-grid">
              {fields.map(({ key, label }) => {
                const isNumber = ['blocks_count','block_age_years','storeys_count','block_height_m'].includes(key);
                const isMultiline = ['main_usage','as_built_records','maintenance_history','known_past_defects','resurfacing_past_defects'].includes(key);
                return (
                  <React.Fragment key={key}>
                    <div className="info-label-cell">{label}</div>
                    <div className="info-value-cell">
                      {isMultiline ? (
                        <textarea
                          rows={3}
                          value={form?.[key] ?? ''}
                          onChange={(e) => handleChange(key, e.target.value)}
                          className="cell-input"
                          placeholder={`Enter ${label.toLowerCase()}`}
                        />
                      ) : (
                        <input
                          type={isNumber ? 'number' : 'text'}
                          value={form?.[key] ?? ''}
                          onChange={(e) => handleChange(
                            key,
                            isNumber
                              ? (key === 'block_height_m' ? parseFloat(e.target.value || '0') : parseInt(e.target.value || '0', 10))
                              : e.target.value
                          )}
                          className="cell-input"
                          placeholder={`Enter ${label.toLowerCase()}`}
                        />
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </>
      )}

      {activeTab === 'FacadeArea' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>Façade Area and 10% Close Range</h3>
            <div className="actions">
              <button onClick={addFacadeAreaRow} disabled={saving} className="btn secondary">Add Row</button>
              <button onClick={handleSave} disabled={saving} className="btn primary">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>

          <div className="info-card">
            <div style={{ padding: 16, overflowX: 'auto' }}>
              <table className="facade-area-table">
                <thead>
                  <tr>
                    <th rowSpan="2">Elevation</th>
                    <th colSpan="2">Total Surface Area (100%)</th>
                    <th rowSpan="2">Total (m²) Area</th>
                    <th rowSpan="2">Tactile Point</th>
                    <th colSpan="2">Area Of Surface (10%)</th>
                    <th rowSpan="2">Min 10% Area</th>
                    <th rowSpan="2">Action</th>
                  </tr>
                  <tr>
                    <th>Length (m)</th>
                    <th>Height (m)</th>
                    <th>Length (m)</th>
                    <th>Height (m)</th>
                  </tr>
                </thead>
                <tbody>
                  {getFacadeAreaRows().map((row, index) => {
                    const normalized = normalizeFacadeAreaRow(row);
                    return (
                      <tr key={index}>
                        <td>
                          <input
                            className="cell-input"
                            value={normalized.elevation}
                            onChange={(e) => updateFacadeAreaRow(index, 'elevation', e.target.value)}
                            placeholder="Elevation"
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.total_length_m ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'total_length_m', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.height_m ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'height_m', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.total_area_m2 ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'total_area_m2', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.tactile_point ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'tactile_point', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.tactile_length_m ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'tactile_length_m', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.tactile_height_m ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'tactile_height_m', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <input
                            className="cell-input"
                            type="number"
                            value={normalized.min_10_area_m2 ?? ''}
                            onChange={(e) => updateFacadeAreaRow(index, 'min_10_area_m2', toNumber(e.target.value))}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn"
                            disabled={saving}
                            onClick={() => removeFacadeAreaRow(index)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="facade-area-caption">Table 1. Façade Area and 10% Closed Ranged</div>
            </div>
          </div>

          <div className="info-card" style={{ marginTop: 16 }}>
            <div style={{ padding: 16 }}>
              <RichTextEditor
                value={form?.facade_area_notes || DEFAULT_FACADE_AREA_NOTES}
                onChange={(html) => handleChange('facade_area_notes', html)}
              />
            </div>
          </div>
        </>
      )}

      {activeTab === '1.2' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>1.2 Overview of building elevations</h3>
            <div className="actions">
              {projectId && (
                <button onClick={handleCopyPlansToProject} disabled={saving} className="btn secondary" title="Copy these plans to all other buildings in the project">
                  Copy to All Buildings
                </button>
              )}
            </div>
          </div>
          <div style={{ padding: '0 16px', marginBottom: 16, fontSize: '14px' }}>
            a) Plan view (as-built drawing if available)
          </div>
          <div className="info-card">
            <div style={{ padding: '12px 16px' }}>
              <AnnexEditor
                title={newPlanName}
                setTitle={setNewPlanName}
                files={newPlanFiles}
                setFiles={setNewPlanFiles}
                titleLabel="Plan title"
                accept="image/*,.pdf"
              />
              <div style={{ marginTop: 12 }}>
                <div className="info-label-cell">Description</div>
                <div className="info-value-cell">
                  <textarea
                    rows={3}
                    className="cell-input"
                    placeholder="Optional description for this plan"
                    value={newPlanDesc}
                    onChange={(e) => setNewPlanDesc(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <button className="btn primary" onClick={handleCreatePlan} disabled={scope === 'project' || saving || !newPlanName || (newPlanFiles.length === 0)}>
                  {saving ? 'Saving...' : 'Add Plan'}
                </button>
                {scope === 'project' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Switch to Building view to add a plan.</div>
                )}
              </div>
            </div>

            <div>
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  className="cell-input"
                  placeholder="Filter by plan title"
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  style={{ maxWidth: 300 }}
                />
              </div>
              {(() => {
                const relevantPlans = plansToShow.filter(isSection12SitePlan);
                if (relevantPlans.length === 0) {
                  return <div style={{ color: '#666', padding: '0 16px 16px' }}>No plans uploaded yet.</div>;
                }
                const groups = relevantPlans.reduce((acc, item) => {
                    const t = (item.name || 'Untitled').trim();
                    if (!acc[t]) acc[t] = [];
                    acc[t].push(item);
                    return acc;
                  }, {});
                  let titles = Object.keys(groups);
                  if (planFilter.trim()) {
                    const q = planFilter.trim().toLowerCase();
                    titles = titles.filter((t) => t.toLowerCase().includes(q));
                  }
                  return (
                    <div className="info-grid">
                      {titles.map((t) => (
                        <React.Fragment key={t}>
                          <div className="info-label-cell" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
                            {editingGroupKey === t ? (
                              <input
                                className="cell-input"
                                style={{ maxWidth: 240 }}
                                value={editGroupTitle}
                                onChange={(e) => setEditGroupTitle(e.target.value)}
                                placeholder="Plan title"
                              />
                            ) : (
                              <span>{t}</span>
                            )}
                            {editingGroupKey === t ? (
                              <>
                                <button
                                  className="btn primary"
                                  style={{ padding: '4px 8px', fontSize: 12 }}
                                  onClick={async () => {
                                    try {
                                      setSaving(true);
                                      setError('');
                                      const items = groups[t] || [];
                                      for (const p of items) {
                                        const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                        await updateBuildingPlan(bId, p.id, { name: editGroupTitle, description: editGroupDesc });
                                      }
                                      if (scope === 'project') {
                                        const buildings = projectBuildings || [];
                                        const nameMap = buildingNameById || {};
                                        const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                        const planResults = await Promise.all(planPromises);
                                        const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                        setProjectPlans(aggregatedPlans);
                                      } else {
                                        const planList = await getBuildingPlans(effectiveBuildingId);
                                        setPlans(planList || []);
                                      }
                                      setSuccess('Plan updated');
                                      setEditingGroupKey(null);
                                      setEditGroupTitle('');
                                      setEditGroupDesc('');
                                    } catch (e) {
                                      console.error('Error updating plan group:', e);
                                      setError('Failed to update plan');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }}
                                >Save</button>
                                <button
                                  className="btn"
                                  style={{ padding: '4px 8px', fontSize: 12 }}
                                  onClick={() => {
                                    setEditingGroupKey(null);
                                    setEditGroupTitle('');
                                    setEditGroupDesc('');
                                  }}
                                >Cancel</button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn"
                                  style={{ padding: '4px 8px', fontSize: 12 }}
                                  onClick={() => {
                                    setEditingGroupKey(t);
                                    setEditGroupTitle(t);
                                    setEditGroupDesc(groups[t][0]?.description || '');
                                  }}
                                >Edit</button>
                                <button
                                  className="btn"
                                  style={{ padding: '4px 8px', fontSize: 12 }}
                                  onClick={async () => {
                                    if (!window.confirm('Delete this plan and all its files?')) return;
                                    try {
                                      setSaving(true);
                                      setError('');
                                      const items = groups[t] || [];
                                      for (const p of items) {
                                        const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                        await deleteBuildingPlan(bId, p.id);
                                      }
                                      if (scope === 'project') {
                                        const buildings = projectBuildings || [];
                                        const nameMap = buildingNameById || {};
                                        const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                        const planResults = await Promise.all(planPromises);
                                        const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                        setProjectPlans(aggregatedPlans);
                                      } else {
                                        const planList = await getBuildingPlans(effectiveBuildingId);
                                        setPlans(planList || []);
                                      }
                                      setSuccess('Plan deleted');
                                    } catch (e) {
                                      console.error('Error deleting plan group:', e);
                                      setError('Failed to delete plan');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }}
                                >Delete</button>
                              </>
                            )}
                          </div>
                          <div className="info-value-cell">
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center' }}>
                              {groups[t]
                                .filter((p) => !!p.file_url)
                                .map((p, idx) => {
                                  const absoluteUrl = toAbsoluteUrl(p.file_url);
                                  const isSelectedSitePlan = scope === 'building' && sitePlanImageUrl && sitePlanImageUrl === absoluteUrl;
                                  return (
                                    <div
                                      key={p.id}
                                      style={{ position: 'relative' }}
                                      draggable={scope === 'building'}
                                      onDragStart={(e) => {
                                        if (scope !== 'building') return;
                                        dragIndexRef.current = idx;
                                        dragGroupRef.current = t;
                                        e.dataTransfer.effectAllowed = 'move';
                                      }}
                                      onDragOver={(e) => {
                                        if (scope !== 'building') return;
                                        e.preventDefault();
                                        overIndexRef.current = idx;
                                        e.dataTransfer.dropEffect = 'move';
                                      }}
                                      onDrop={(e) => {
                                        if (scope !== 'building') return;
                                        e.preventDefault();
                                        const from = dragIndexRef.current;
                                        const to = overIndexRef.current;
                                        const g = dragGroupRef.current;
                                        if (g === t) {
                                          reorderGroup(plans, setPlans, t, from, to);
                                        }
                                        dragIndexRef.current = null;
                                        overIndexRef.current = null;
                                        dragGroupRef.current = null;
                                      }}
                                      onDragEnd={() => {
                                        dragIndexRef.current = null;
                                        overIndexRef.current = null;
                                        dragGroupRef.current = null;
                                      }}
                                    >
                                      {isPdfUrl(p.file_url) ? (
                                        <a href={absoluteUrl} target="_blank" rel="noreferrer" className="btn">Open PDF</a>
                                      ) : (
                                        <FilePreview
                                          url={p.file_url}
                                          alt={p.name}
                                          width={160}
                                          height={100}
                                          objectFit="contain"
                                          onClick={() => {
                                            setLightboxGroup(groups[t].filter((g) => !!g.file_url && !isPdfUrl(g.file_url)));
                                            setLightboxIndex(idx);
                                            setLightboxOpen(true);
                                          }}
                                        />
                                      )}
                                    {scope === 'building' && !isPdfUrl(p.file_url) && (
                                      <button
                                        className="btn"
                                        style={{ position: 'absolute', bottom: 4, left: 4, padding: '4px 8px', fontSize: 12 }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleChangeSitePlan(absoluteUrl);
                                        }}
                                      >
                                        {isSelectedSitePlan ? 'Selected site plan' : 'Use as site plan'}
                                      </button>
                                    )}
                                      <button
                                        className="btn"
                                        style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                        onClick={async () => {
                                          if (!window.confirm('Delete this file from the plan?')) return;
                                          try {
                                            setSaving(true);
                                            setError('');
                                            const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                            await deleteBuildingPlan(bId, p.id);
                                            if (scope === 'project') {
                                              const buildings = projectBuildings || [];
                                              const nameMap = buildingNameById || {};
                                              const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                              const planResults = await Promise.all(planPromises);
                                              const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                              setProjectPlans(aggregatedPlans);
                                            } else {
                                              const planList = await getBuildingPlans(effectiveBuildingId);
                                              setPlans(planList || []);
                                            }
                                            setSuccess('File deleted');
                                          } catch (e) {
                                            console.error('Error deleting plan file:', e);
                                            setError('Failed to delete file');
                                          } finally {
                                            setSaving(false);
                                          }
                                        }}
                                      >Delete</button>
                                      {!String(p.file_url).match(/\.pdf$/i) && (
                                        <button
                                          className="btn"
                                          style={{ position: 'absolute', bottom: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openAnnotateForGroup(groups[t], idx);
                                          }}
                                        >Annotate</button>
                                      )}
                                      {scope === 'project' && p._building_name && (
                                        <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 6 }}>
                                          {p._building_name}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                            {editingGroupKey === t ? (
                              <div style={{ marginTop: 8 }}>
                                <textarea
                                  rows={3}
                                  className="cell-input"
                                  placeholder="Plan description"
                                  value={editGroupDesc}
                                  onChange={(e) => setEditGroupDesc(e.target.value)}
                                />
                              </div>
                            ) : (
                              groups[t][0]?.description ? (
                                <div style={{ marginTop: 8, color: '#555', fontSize: 13 }}>{groups[t][0].description}</div>
                              ) : null
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div style={{ padding: '0 16px', marginBottom: 16, marginTop: 24, fontSize: '14px' }}>
              b) All elevation views (photos)
            </div>
            <div className="info-card">
              <div style={{ padding: '12px 16px' }}>
                <AnnexEditor
                  title={newElevationViewName}
                  setTitle={setNewElevationViewName}
                  files={newElevationViewFiles}
                  setFiles={setNewElevationViewFiles}
                  titleLabel="Elevation"
                  accept="image/*"
                />
                <div style={{ marginTop: 12 }}>
                  <button className="btn primary" onClick={handleAddElevationView} disabled={scope === 'project' || saving || !newElevationViewName || (newElevationViewFiles.length === 0)}>
                    {saving ? 'Saving...' : 'Add Elevation View'}
                  </button>
                  {scope === 'project' && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Switch to Building view to add an elevation view.</div>
                  )}
                </div>
              </div>
              <div>
                {(() => {
                  const elevationPlans = plansToShow.filter(p => (p.name || '').trim().startsWith('Elevation View -'));
                  if (elevationPlans.length === 0) {
                    return <div style={{ color: '#666', padding: '0 16px 16px' }}>No elevation views uploaded yet.</div>;
                  }
                  const groups = elevationPlans.reduce((acc, item) => {
                    const t = (item.name || 'Untitled').trim();
                    if (!acc[t]) acc[t] = [];
                    acc[t].push(item);
                    return acc;
                  }, {});
                  const titles = Object.keys(groups);
                  return (
                    <div className="info-grid">
                      {titles.map((t) => (
                        <React.Fragment key={t}>
                          <div className="info-label-cell" style={{ fontWeight: 'bold' }}>{t.replace('Elevation View - ', '')}</div>
                          <div className="info-value-cell">
                             <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center' }}>
                               {groups[t].filter(p => !!p.file_url).map((p, idx) => (
                                 <div key={p.id} style={{ position: 'relative' }}>
                                    <img
                                      src={toAbsoluteUrl(p.file_url)}
                                      alt={p.name}
                                      style={{ width: 160, height: 100, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#f8fafc' }}
                                      onClick={() => {
                                        setLightboxGroup(groups[t].filter((g) => !!g.file_url && !g.file_url.match(/\.pdf$/i)));
                                        setLightboxIndex(idx);
                                        setLightboxOpen(true);
                                      }}
                                    />
                                    <button
                                      className="btn"
                                      style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                      onClick={async () => {
                                        if (!window.confirm('Delete this file?')) return;
                                        try {
                                          setSaving(true);
                                          const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                          await deleteBuildingPlan(bId, p.id);
                                          if (scope === 'project') {
                                            // Refresh project plans logic if needed, but for now just refresh building plans is safer or re-fetch project
                                            // Copying refresh logic from 1.2a
                                            const buildings = projectBuildings || [];
                                            const nameMap = buildingNameById || {};
                                            const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                            const planResults = await Promise.all(planPromises);
                                            const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                            setProjectPlans(aggregatedPlans);
                                          } else {
                                            const planList = await getBuildingPlans(effectiveBuildingId);
                                            setPlans(planList || []);
                                          }
                                          setSuccess('File deleted');
                                        } catch (e) {
                                          console.error('Error deleting file:', e);
                                        } finally {
                                          setSaving(false);
                                        }
                                      }}
                                    >Delete</button>
                                    <button
                                      className="btn"
                                      style={{ position: 'absolute', bottom: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openAnnotateForGroup(groups[t], idx);
                                      }}
                                    >Annotate</button>
                                    {scope === 'project' && p._building_name && (
                                      <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 6 }}>
                                        {p._building_name}
                                      </div>
                                    )}
                                 </div>
                               ))}
                             </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
        </>
      )}

      {activeTab === '1.3' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>1.3 Description of facade system</h3>
          </div>
            <div className="info-card">
              <div style={{ padding: '12px 16px' }}>
                {/* Title removed as requested */}
                {/* Additional facade detail fields */}
                <div className="info-grid" style={{ marginTop: 12 }}>
                  <div className="info-label-cell">Elevation</div>
                  <div className="info-value-cell">
                    <input
                      className="cell-input"
                      placeholder="e.g., North, South, East, West"
                      type="text"
                      value={newFacadeElevation}
                      onChange={(e) => setNewFacadeElevation(e.target.value)}
                    />
                  </div>
                  <div className="info-label-cell">Facade type</div>
                  <div className="info-value-cell">
                    <input
                      className="cell-input"
                      placeholder="e.g., Curtain wall, Cladding"
                      type="text"
                      value={newFacadeType}
                      onChange={(e) => setNewFacadeType(e.target.value)}
                    />
                  </div>
                  <div className="info-label-cell">Connection</div>
                  <div className="info-value-cell">
                    <input
                      className="cell-input"
                      placeholder="e.g., Bracket fixings, Bolt connections"
                      type="text"
                      value={newFacadeConnection}
                      onChange={(e) => setNewFacadeConnection(e.target.value)}
                    />
                  </div>
                </div>
                {/* Files uploader moved below */}
                <div className="info-grid" style={{ marginTop: 12 }}>
                  <div className="info-label-cell">Files</div>
                  <div className="info-value-cell">
                    <FileDropzone
                      files={newFacadeFiles}
                      setFiles={setNewFacadeFiles}
                      accept="image/*,.pdf"
                    />
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                <button className="btn primary" onClick={handleAddFacade} disabled={scope === 'project' || saving || ((!(newFacadeElevation?.trim()) && !(newFacadeType?.trim()) && !(newFacadeConnection?.trim())) || (newFacadeFiles.length === 0))}>
                  {saving ? 'Saving...' : 'Add Facade'}
                </button>
                {scope === 'project' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Switch to Building view to add a facade.</div>
                )}
              </div>
            </div>

            <div>
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  className="cell-input"
                  placeholder="Filter by facade title"
                  value={facadeFilter}
                  onChange={(e) => setFacadeFilter(e.target.value)}
                  style={{ maxWidth: 300 }}
                />
              </div>
              {(() => {
                const facadePlans = plansToShow.filter((p) => ((p.name || '').trim().toLowerCase().startsWith('facade')) || ((p.description || '').toLowerCase().includes('facade')));
                if (facadePlans.length === 0) {
                  return <div style={{ color: '#666', padding: '0 16px 16px' }}>No facades added yet.</div>;
                }
                const groups = facadePlans.reduce((acc, item) => {
                  const t = (item.name || 'Facade').trim();
                  if (!acc[t]) acc[t] = [];
                  acc[t].push(item);
                  return acc;
                }, {});
                let titles = Object.keys(groups);
                if (facadeFilter.trim()) {
                  const q = facadeFilter.trim().toLowerCase();
                  titles = titles.filter((t) => t.toLowerCase().includes(q));
                }
                return (
                  <div className="info-grid">
                    {titles.map((t) => (
                      <React.Fragment key={t}>
                        <div className="info-label-cell" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
                          {editingGroupKey === t ? (
                            <>
                              <input
                                className="cell-input"
                                style={{ maxWidth: 240 }}
                                value={editGroupTitle}
                                onChange={(e) => setEditGroupTitle(e.target.value)}
                                placeholder="Facade title"
                              />
                              <button
                                className="btn primary"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                onClick={async () => {
                                  try {
                                    setSaving(true);
                                    setError('');
                                    const items = groups[t] || [];
                                    for (const p of items) {
                                      const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                      await updateBuildingPlan(bId, p.id, { name: editGroupTitle, description: editGroupDesc });
                                    }
                                    if (scope === 'project') {
                                      const buildings = projectBuildings || [];
                                      const nameMap = buildingNameById || {};
                                      const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                      const planResults = await Promise.all(planPromises);
                                      const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                      setProjectPlans(aggregatedPlans);
                                    } else {
                                      const planList = await getBuildingPlans(effectiveBuildingId);
                                      setPlans(planList || []);
                                    }
                                    setSuccess('Facade updated');
                                    setEditingGroupKey(null);
                                    setEditGroupTitle('');
                                    setEditGroupDesc('');
                                  } catch (e) {
                                    console.error('Error updating facade group:', e);
                                    setError('Failed to update facade');
                                  } finally {
                                    setSaving(false);
                                  }
                                }}
                              >Save</button>
                              <button
                                className="btn"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                onClick={() => {
                                  setEditingGroupKey(null);
                                  setEditGroupTitle('');
                                  setEditGroupDesc('');
                                }}
                              >Cancel</button>
                            </>
                          ) : (
                            <>
                              <span>{t}</span>
                              <button
                                className="btn"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                onClick={() => {
                                  setEditingGroupKey(t);
                                  setEditGroupTitle(t);
                                  setEditGroupDesc(groups[t][0]?.description || '');
                                }}
                              >Edit</button>
                              <button
                                className="btn"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                onClick={async () => {
                                  if (!window.confirm('Delete this facade and all its files?')) return;
                                  try {
                                    setSaving(true);
                                    setError('');
                                    const items = groups[t] || [];
                                    for (const p of items) {
                                      const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                      await deleteBuildingPlan(bId, p.id);
                                    }
                                    if (scope === 'project') {
                                      const buildings = projectBuildings || [];
                                      const nameMap = buildingNameById || {};
                                      const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                      const planResults = await Promise.all(planPromises);
                                      const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                      setProjectPlans(aggregatedPlans);
                                    } else {
                                      const planList = await getBuildingPlans(effectiveBuildingId);
                                      setPlans(planList || []);
                                    }
                                    setSuccess('Facade deleted');
                                  } catch (e) {
                                    console.error('Error deleting facade group:', e);
                                    setError('Failed to delete facade');
                                  } finally {
                                    setSaving(false);
                                  }
                                }}
                              >Delete</button>
                            </>
                          )}
                        </div>
                        <div className="info-value-cell">
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center' }}>
                            {groups[t]
                              .filter((p) => !!p.file_url)
                              .map((p, idx) => (
                                <div
                                  key={p.id}
                                  style={{ position: 'relative' }}
                                  draggable={scope === 'building'}
                                  onDragStart={(e) => {
                                    if (scope !== 'building') return;
                                    dragIndexRef.current = idx;
                                    dragGroupRef.current = t;
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragOver={(e) => {
                                    if (scope !== 'building') return;
                                    e.preventDefault();
                                    overIndexRef.current = idx;
                                    e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(e) => {
                                    if (scope !== 'building') return;
                                    e.preventDefault();
                                    const from = dragIndexRef.current;
                                    const to = overIndexRef.current;
                                    const g = dragGroupRef.current;
                                    if (g === t) {
                                      reorderGroup(plans, setPlans, t, from, to);
                                    }
                                    dragIndexRef.current = null;
                                    overIndexRef.current = null;
                                    dragGroupRef.current = null;
                                  }}
                                  onDragEnd={() => {
                                    dragIndexRef.current = null;
                                    overIndexRef.current = null;
                                    dragGroupRef.current = null;
                                  }}
                                >
                                  {isPdfUrl(p.file_url)
                                    ? <a href={toAbsoluteUrl(p.file_url)} target="_blank" rel="noreferrer" className="btn">Open PDF</a>
                                    : (
                                      <FilePreview
                                        url={p.file_url}
                                        alt={p.name}
                                        width={160}
                                        height={100}
                                        objectFit="contain"
                                        onClick={() => {
                                          setLightboxGroup(groups[t].filter((g) => !!g.file_url && !isPdfUrl(g.file_url)));
                                          setLightboxIndex(idx);
                                          setLightboxOpen(true);
                                        }}
                                      />
                                    )}
                                  <button
                                    className="btn"
                                    style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                    onClick={async () => {
                                      if (!window.confirm('Delete this file?')) return;
                                      try {
                                        setSaving(true);
                                        const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                        await deleteBuildingPlan(bId, p.id);
                                        if (scope === 'project') {
                                          const buildings = projectBuildings || [];
                                          const nameMap = buildingNameById || {};
                                          const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                          const planResults = await Promise.all(planPromises);
                                          const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                          setProjectPlans(aggregatedPlans);
                                        } else {
                                          const planList = await getBuildingPlans(effectiveBuildingId);
                                          setPlans(planList || []);
                                        }
                                        setSuccess('File deleted');
                                      } catch (e) {
                                        console.error('Error deleting file:', e);
                                      } finally {
                                        setSaving(false);
                                      }
                                    }}
                                  >Delete</button>
                                  {!isPdfUrl(p.file_url) && (
                                    <button
                                      className="btn"
                                      style={{ position: 'absolute', bottom: 4, left: 4, padding: '4px 8px', fontSize: 12 }}
                                      onClick={() => openAnnotateForGroup(groups[t], idx)}
                                    >Annotate</button>
                                  )}
                                </div>
                              ))}
                          </div>
                          {groups[t][0]?.description ? (
                            <div style={{ marginTop: 8, color: '#555', fontSize: 13 }}>{groups[t][0].description}</div>
                          ) : null}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {activeTab === '1.5' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>1.5 Tapping Inspection</h3>
          </div>
          <div className="info-card">
            <div style={{ padding: '12px 16px' }}>
              <AnnexEditor
                title={newTappingName}
                setTitle={setNewTappingName}
                files={newTappingFiles}
                setFiles={setNewTappingFiles}
                titleLabel="Title"
                accept="image/*"
              />
              <div style={{ marginTop: 12 }}>
                <button className="btn primary" onClick={handleAddTappingView} disabled={scope === 'project' || saving || !newTappingName || (newTappingFiles.length === 0)}>
                  {saving ? 'Saving...' : 'Add Tapping Inspection'}
                </button>
                {scope === 'project' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Switch to Building view to add a tapping inspection.</div>
                )}
              </div>
            </div>
            <div>
              {(() => {
                const tappingPlans = plansToShow.filter(p => (p.name || '').trim().startsWith('Tapping Inspection -'));
                if (tappingPlans.length === 0) {
                  return <div style={{ color: '#666', padding: '0 16px 16px' }}>No tapping inspections added yet.</div>;
                }
                const groups = tappingPlans.reduce((acc, item) => {
                  const t = (item.name || 'Untitled').trim();
                  if (!acc[t]) acc[t] = [];
                  acc[t].push(item);
                  return acc;
                }, {});
                const titles = Object.keys(groups);
                return (
                  <div className="info-grid">
                    {titles.map((t) => (
                      <React.Fragment key={t}>
                        <div className="info-label-cell" style={{ fontWeight: 'bold' }}>{t.replace('Tapping Inspection - ', '')}</div>
                        <div className="info-value-cell">
                           <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center' }}>
                             {groups[t].filter(p => !!p.file_url).map((p, idx) => (
                               <div key={p.id} style={{ position: 'relative' }}>
                                  <img
                                    src={toAbsoluteUrl(p.file_url)}
                                    alt={p.name}
                                    style={{ width: 160, height: 100, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#f8fafc' }}
                                    onClick={() => {
                                      setLightboxGroup(groups[t].filter((g) => !!g.file_url && !g.file_url.match(/\.pdf$/i)));
                                      setLightboxIndex(idx);
                                      setLightboxOpen(true);
                                    }}
                                  />
                                  <button
                                    className="btn"
                                    style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                    onClick={async () => {
                                      if (!window.confirm('Delete this file?')) return;
                                      try {
                                        setSaving(true);
                                        const bId = scope === 'project' ? (p.building_id || p._building_id) : effectiveBuildingId;
                                        await deleteBuildingPlan(bId, p.id);
                                        if (scope === 'project') {
                                          const buildings = projectBuildings || [];
                                          const nameMap = buildingNameById || {};
                                          const planPromises = buildings.map(b => getBuildingPlans(b.id).catch(() => []));
                                          const planResults = await Promise.all(planPromises);
                                          const aggregatedPlans = planResults.flat().map(p2 => ({ ...p2, _building_id: p2.building_id, _building_name: nameMap[p2.building_id] }));
                                          setProjectPlans(aggregatedPlans);
                                        } else {
                                          const planList = await getBuildingPlans(effectiveBuildingId);
                                          setPlans(planList || []);
                                        }
                                        setSuccess('File deleted');
                                      } catch (e) {
                                        console.error('Error deleting file:', e);
                                      } finally {
                                        setSaving(false);
                                      }
                                    }}
                                  >Delete</button>
                                  {!String(p.file_url).match(/\.pdf$/i) && (
                                    <button
                                      className="btn"
                                      style={{ position: 'absolute', bottom: 4, left: 4, padding: '4px 8px', fontSize: 12 }}
                                      onClick={() => openAnnotateForGroup(groups[t], idx)}
                                    >Annotate</button>
                                  )}
                                  {scope === 'project' && p._building_name && (
                                    <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 6 }}>
                                      {p._building_name}
                                    </div>
                                  )}
                               </div>
                             ))}
                           </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {annotateOpen && (
        <ImageAnnotationModal
          open={annotateOpen}
          onClose={() => setAnnotateOpen(false)}
          image={annotateImages[annotateIndex]}
          images={annotateImages}
          currentImageIndex={annotateIndex}
          onNavigateImage={(i) => setAnnotateIndex(i)}
          drawingOnly={true}
          onSave={async (result) => {
            const newAnnotations = result.annotations || [];
            // Save to backend as site plan annotations so they appear in PDF 1.4
            const currentImg = annotateImages[annotateIndex];
            if (currentImg && currentImg.file_url) {
                try {
                    setSaving(true);
                    const targetBuildingId = currentImg.building_id || effectiveBuildingId;
                    const current = await apiService.getSitePlanAnnotations(targetBuildingId).catch(() => ({}));
                    const existingByPlan = current?.by_plan_url || {};
                    const planKey = normalizePlanUrlKey(currentImg.file_url) || currentImg.file_url;
                    const currentContext = String(
                      currentImg.annotation_context ||
                      currentImg.imageType ||
                      result.imageType ||
                      'drone'
                    ).trim().toLowerCase();
                    const nextByPlan = {};
                    const equivalentExisting = [];
                    Object.entries(existingByPlan).forEach(([url, anns]) => {
                      if (!Array.isArray(anns)) return;
                      if (isSamePlanUrl(url, planKey)) {
                        equivalentExisting.push(...anns);
                        return;
                      }
                      const normalizedUrl = normalizePlanUrlKey(url) || url;
                      nextByPlan[normalizedUrl] = anns;
                    });
                    const preservedPlanAnnotations = equivalentExisting.filter((ann) => {
                      const renderStyle = String(ann?.render_style || ann?.renderStyle || '').trim().toLowerCase();
                      const annImageId = ann?.image_id;
                      const annContext = String(ann?.context || 'drone').trim().toLowerCase();
                      const isBuildingInfoOutline = annImageId == null && renderStyle === 'outline' && annContext === currentContext;
                      return !isBuildingInfoOutline;
                    });
                    const updatedForUrl = [
                      ...preservedPlanAnnotations,
                      ...newAnnotations.map((a, annIdx) => ({
                        id: String(a.id || `${currentImg.id}_${annIdx + 1}`),
                        image_id: null,
                        label: String(a.text || a.label || ''),
                        x: typeof a.x === 'number' ? a.x : (typeof a.startX === 'number' ? a.startX : 0),
                        y: typeof a.y === 'number' ? a.y : (typeof a.startY === 'number' ? a.startY : 0),
                        width: typeof a.width === 'number' ? a.width : (typeof a.startX === 'number' && typeof a.endX === 'number' ? a.endX - a.startX : 0),
                        height: typeof a.height === 'number' ? a.height : (typeof a.startY === 'number' && typeof a.endY === 'number' ? a.endY - a.startY : 0),
                        startX: typeof a.startX === 'number' ? a.startX : undefined,
                        startY: typeof a.startY === 'number' ? a.startY : undefined,
                        endX: typeof a.endX === 'number' ? a.endX : undefined,
                        endY: typeof a.endY === 'number' ? a.endY : undefined,
                        severity: a.severity || a.severityCategory || 'repair',
                        context: a.context || currentContext,
                        level: currentImg.floor_name || currentImg.floor || '',
                        type: a.type || 'square',
                        coordinate_mode: 'pixel',
                        render_style: 'outline',
                        suppress_label: !String(a.text || a.label || '').trim()
                      }))
                    ];
                    const dedup = new Map();
                    updatedForUrl.forEach((ann) => {
                      const key = `${String(ann?.image_id || ann?.id || ann?.label || '')}|${ann?.context || 'drone'}`;
                      dedup.set(key, ann);
                    });
                    const payload = {
                      ...current,
                      by_plan_url: {
                        ...nextByPlan,
                        [planKey]: Array.from(dedup.values()),
                      },
                    };
                    await saveSitePlanAnnotations(targetBuildingId, payload);
                    setSuccess('Annotation saved to database');
                } catch (e) {
                    console.error('Failed to save annotation:', e);
                    setError('Failed to save annotation');
                } finally {
                    setSaving(false);
                }
            } else {
                setSuccess('Annotation saved (Local only)');
            }
          }}
          sitePlanImageUrl={sitePlanImageUrl}
          sitePlanCandidates={sitePlanCandidates}
          onChangeSitePlan={handleChangeSitePlan}
          buildingId={annotateImages[annotateIndex]?.building_id || effectiveBuildingId}
          imageType={annotateImages[annotateIndex]?.annotation_context || 'drone'}
          showSitePlanPanel={sitePlanCandidates.length > 0}
        />
      )}

      {activeTab === '2.1' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>2.1 Diary and Scope of Visual Inspection</h3>
            <div className="actions">
              <button onClick={handleSaveInspectionDraft} className="btn primary" disabled={saving}>
                Save
              </button>
            </div>
          </div>
          <div className="info-card">
            <div className="info-grid">
              <div className="info-label-cell">Time and date of inspection (Start)</div>
              <div className="info-value-cell">
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Start</label>
                  <input
                    type="datetime-local"
                    className="cell-input"
                    value={inspectionStart}
                    onChange={(e) => {
                      setInspectionStart(e.target.value);
                      updateInspectionTimeDatesFromPickers(e.target.value);
                    }}
                  />
                </div>
                <textarea
                  rows={2}
                  className="cell-input"
                  placeholder="e.g., 5 April 2025, 9am to 1pm"
                  value={inspection.time_dates}
                  onChange={(e) => handleInspectionChange('time_dates', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Duration of inspection</div>
              <div className="info-value-cell">
                <input
                  type="text"
                  className="cell-input"
                  placeholder="e.g., 4 hours"
                  value={inspection.duration}
                  onChange={(e) => handleInspectionChange('duration', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Location of inspection</div>
              <div className="info-value-cell">
                <input
                  type="text"
                  className="cell-input"
                  placeholder="e.g., 99 Meyer Rd, Singapore 437920"
                  value={inspection.location}
                  onChange={(e) => handleInspectionChange('location', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Methodology of carrying out the 100% visual inspection</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="e.g., Drone. If drone not used, explain why and provide vantage locations in Annex A."
                  value={inspection.methodology}
                  onChange={(e) => handleInspectionChange('methodology', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Names, addresses, contact details of others involved</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="e.g., Drone service provider/specialists/consultants (list if any)"
                  value={inspection.participants}
                  onChange={(e) => handleInspectionChange('participants', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Particulars of Façade Inspector engaged (if any)</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="e.g., Name, Registration no., Expiry date"
                  value={inspection.inspector_particulars}
                  onChange={(e) => handleInspectionChange('inspector_particulars', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Areas not covered and reasons (if any)</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="Describe any non-coverage"
                  value={inspection.non_coverage_description}
                  onChange={(e) => handleInspectionChange('non_coverage_description', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Discrepancies/deviations from approved plans</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="Highlight discrepancies (with references to drawings/photos)"
                  value={inspection.discrepancies}
                  onChange={(e) => handleInspectionChange('discrepancies', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Coverage statement</div>
              <div className="info-value-cell">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={inspection.covered_all}
                    onChange={(e) => handleInspectionChange('covered_all', e.target.checked)}
                  />
                  <span>The visual inspection has covered all façade system within the buildings.</span>
                </label>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === '3.1' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>3.1 Diary and Scope of Close-range Inspection</h3>
            <div className="actions">
              <button onClick={handleSaveCloseRange} className="btn primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
          <div className="info-card">
            <div className="info-grid">
              <div className="info-label-cell">Basis of selection of locations/areas</div>
              <div className="info-value-cell">
                <input
                  type="text"
                  className="cell-input"
                  placeholder="e.g., North, South, West, East"
                  value={closeRange.basis_selection}
                  onChange={(e) => handleCloseRangeChange('basis_selection', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Drawings/plan of selected areas as record</div>
              <div className="info-value-cell">
                <input
                  type="text"
                  className="cell-input"
                  placeholder="e.g., See Annex B"
                  value={closeRange.drawings_plan}
                  onChange={(e) => handleCloseRangeChange('drawings_plan', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Time and date of Close-range inspection (Start)</div>
              <div className="info-value-cell">
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Start</label>
                  <input
                    type="datetime-local"
                    className="cell-input"
                    value={closeRangeStart}
                    onChange={(e) => {
                      setCloseRangeStart(e.target.value);
                      updateCloseRangeTimeDatesFromPickers(e.target.value);
                    }}
                  />
                </div>
                <textarea
                  rows={2}
                  className="cell-input"
                  placeholder="e.g., 10-11 January 2025, 9am to 5pm"
                  value={closeRange.time_dates}
                  onChange={(e) => handleCloseRangeChange('time_dates', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Duration of inspection</div>
              <div className="info-value-cell">
                <input
                  type="text"
                  className="cell-input"
                  placeholder="e.g., 2 days"
                  value={closeRange.duration}
                  onChange={(e) => handleCloseRangeChange('duration', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Methodology of carrying out the Close-range inspection</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="Describe tools, equipment, inspection of concealed fixings, and CP instructions"
                  value={closeRange.methodology}
                  onChange={(e) => handleCloseRangeChange('methodology', e.target.value)}
                />
              </div>

              <div className="info-label-cell">Particulars of Façade Inspector engaged</div>
              <div className="info-value-cell">
                <textarea
                  rows={3}
                  className="cell-input"
                  placeholder="e.g., Name, Registration no., Expiry date"
                  value={closeRange.inspector_particulars}
                  onChange={(e) => handleCloseRangeChange('inspector_particulars', e.target.value)}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'Annex' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>Annex — Upload files and notes</h3>
            <div className="actions">
              <button
                className="btn primary"
                disabled={scope === 'project' || saving || !annexTitle || !hasValidAnnexUpload}
                onClick={async () => {
                  try {
                    if (!annexTitle || !hasValidAnnexUpload) {
                      setError('Please select an annex title and upload at least one image or PDF.');
                      return;
                    }
                    setSaving(true);
                    setError('');
                    setSuccess('');
                    await uploadAnnexFolder(
                      effectiveBuildingId,
                      (annexFiles || []).filter(isSupportedAnnexFile),
                      annexTitle,
                      annexContent || ''
                    );
                    await refreshAnnexList();
                    setAnnexTitle('');
                    setAnnexContent('');
                    setAnnexFiles([]);
                    // Refresh annex master (to remove selected from options)
                    try {
                      const resp = await apiService.listAnnexMaster();
                      setAnnexMaster(resp.items || []);
                    } catch {}
                    setSuccess('Annex saved successfully');
                  } catch (e) {
                    console.error('Error saving annex:', e);
                    setError(e?.message || 'Failed to save annex');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Annex'}
              </button>
              {scope === 'project' && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Switch to Building view to save annex.</div>
              )}
            </div>
          </div>
          <div className="info-card">
            <div style={{ padding: '12px 16px' }}>
              <AnnexEditor
                title={annexTitle}
                setTitle={setAnnexTitle}
                files={annexFiles}
                setFiles={setAnnexFiles}
                accept="image/*,.pdf"
                options={(() => {
                  const used = new Set((annexList || []).map(a => (a.title || '').toLowerCase()));
                  return (annexMaster || []).filter(m => !used.has((m.title || '').toLowerCase()));
                })()}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                Upload at least one image or PDF to insert an annex.
              </div>
              <div style={{ marginTop: 12 }}>
                <div className="info-label-cell">Notes / description</div>
                <div className="info-value-cell">
                  <textarea
                    rows={3}
                    className="cell-input"
                    placeholder="Optional notes to accompany the image"
                    value={annexContent}
                    onChange={(e) => setAnnexContent(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb' }}>
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  className="cell-input"
                  placeholder="Filter by title"
                  value={annexFilter}
                  onChange={(e) => setAnnexFilter(e.target.value)}
                  style={{ maxWidth: 300 }}
                />
              </div>
              {sortedAnnexesToShow.length === 0 ? (
                <div style={{ padding: 16, color: '#666' }}>No annex items yet.</div>
              ) : (
                (() => {
                  const groups = sortedAnnexesToShow.reduce((acc, item) => {
                    const t = (item.title || 'Untitled').trim();
                    if (!acc[t]) acc[t] = [];
                    acc[t].push(item);
                    return acc;
                  }, {});
                  let titles = Object.keys(groups).sort((a, b) => {
                    const rankDiff = annexLetterRank(a) - annexLetterRank(b);
                    if (rankDiff !== 0) return rankDiff;
                    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
                  });
                  if (annexFilter.trim()) {
                    const q = annexFilter.trim().toLowerCase();
                    titles = titles.filter((t) => t.toLowerCase().includes(q));
                  }
                  return (
                    <div className="info-grid">
                      {titles.map((t) => (
                        <React.Fragment key={t}>
                          <div className="info-label-cell" style={{ fontWeight: 'bold' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <span>{t}</span>
                              <button
                                className="btn"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                disabled={saving}
                                onClick={() => handleDeleteAnnexItems(groups[t])}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          <div className="info-value-cell">
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center' }}>
                              {groups[t]
                                .filter((a) => !!a.file_url)
                                .map((a, idx) => (
                                  <div key={a.id} style={{ position: 'relative' }}>
                                    <FilePreview
                                      url={a.file_url}
                                      alt={a.title}
                                      width={160}
                                      height={100}
                                      objectFit="contain"
                                      onClick={() => {
                                        setLightboxGroup(groups[t].filter((g) => !!g.file_url));
                                        setLightboxIndex(idx);
                                        setLightboxOpen(true);
                                      }}
                                    />
                                    {scope === 'project' && a._building_name && (
                                      <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 6 }}>
                                        {a._building_name}
                                      </div>
                                    )}
                                    <button
                                      className="btn"
                                      style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                                      disabled={saving}
                                      onClick={() => handleDeleteAnnexItems([a])}
                                    >Delete</button>
                                  </div>
                                ))}
                            </div>
                            {groups[t][0]?.content ? (
                              <div style={{ marginTop: 8, color: '#555', fontSize: 13 }}>{groups[t][0].content}</div>
                            ) : null}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          </div>

          {lightboxOpen && (
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
              }}
              onClick={() => setLightboxOpen(false)}
            >
              <div style={{ position: 'relative', padding: 12, background: 'transparent' }} onClick={(e) => e.stopPropagation()}>
                {isPdfUrl(lightboxGroup[lightboxIndex]?.file_url) ? (
                  <iframe
                    src={toAbsoluteUrl(lightboxGroup[lightboxIndex]?.file_url)}
                    title={lightboxGroup[lightboxIndex]?.title}
                    style={{ width: '90vw', height: '80vh', border: 0, borderRadius: 8, background: '#fff' }}
                  />
                ) : (
                  <img
                    src={toAbsoluteUrl(lightboxGroup[lightboxIndex]?.file_url)}
                    alt={lightboxGroup[lightboxIndex]?.title}
                    style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
                  />
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <button className="btn" onClick={() => setLightboxOpen(false)}>Close</button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn"
                      disabled={lightboxIndex <= 0}
                      onClick={() => setLightboxIndex((i) => Math.max(0, i - 1))}
                    >Prev</button>
                    <button
                      className="btn"
                      disabled={lightboxIndex >= lightboxGroup.length - 1}
                      onClick={() => setLightboxIndex((i) => Math.min(lightboxGroup.length - 1, i + 1))}
                    >Next</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'UAS' && (
        <>
          <div className="page-header" style={{ marginTop: 0 }}>
            <h3>UAS Inspection Report</h3>
            <div className="actions">
              <button
                className="btn primary"
                disabled={scope === 'project' || saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    setError('');
                    setSuccess('');
                    await apiService.saveUASReport(effectiveBuildingId, uas);
                    setSuccess('UAS report saved');
                  } catch (e) {
                    console.error('Failed to save UAS report', e);
                    setError('Failed to save UAS report');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save UAS'}
              </button>
              {scope === 'project' && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Switch to Building view to save UAS.</div>
              )}
            </div>
          </div>
          <div className="info-card">
            <div className="info-grid" style={{ padding: '12px 16px' }}>
              <div className="info-label-cell">Flight Location</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.flight_location} onChange={(e) => setUas({ ...uas, flight_location: e.target.value })} />
              </div>
              <div className="info-label-cell">Flight Date & Time</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.flight_datetime} onChange={(e) => setUas({ ...uas, flight_datetime: e.target.value })} placeholder="e.g., 26 Nov 2025, 10.30 am – 12.30 pm" />
              </div>
              <div className="info-label-cell">Purpose</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.purpose} onChange={(e) => setUas({ ...uas, purpose: e.target.value })} />
              </div>
              <div className="info-label-cell">CAAS Activity Permit Reference number</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.caas_activity_permit} onChange={(e) => setUas({ ...uas, caas_activity_permit: e.target.value })} />
              </div>
              <div className="info-label-cell">Areas of Operation</div>
              <div className="info-value-cell">
                <textarea rows={2} className="cell-input" value={uas.areas_of_operation} onChange={(e) => setUas({ ...uas, areas_of_operation: e.target.value })} placeholder="Coordinates, Latitude/Longitude (source)" />
              </div>
              <div className="info-label-cell">Media & file format</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.media_file_format} onChange={(e) => setUas({ ...uas, media_file_format: e.target.value })} />
              </div>
              <div className="info-label-cell">UA(s) model</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.uas_model} onChange={(e) => setUas({ ...uas, uas_model: e.target.value })} />
              </div>
              <div className="info-label-cell">Tethered UA (if applicable)</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.tethered_ua} onChange={(e) => setUas({ ...uas, tethered_ua: e.target.value })} />
              </div>
            </div>
            <div className="info-grid" style={{ padding: '12px 16px' }}>
              <div className="info-label-cell" style={{ fontWeight: 'bold', gridColumn: '1 / span 2', background: '#eee', padding: 8 }}>B. Building Information</div>
              <div className="info-label-cell">B.1 – Client Information</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.client_information} onChange={(e) => setUas({ ...uas, client_information: e.target.value })} />
              </div>
              <div className="info-label-cell">B.2 – Address</div>
              <div className="info-value-cell">
                <input className="cell-input" value={uas.client_address} onChange={(e) => setUas({ ...uas, client_address: e.target.value })} />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BuildingInfo;
